import styled from 'styled-components';

export const HeroSection = styled.section`
  width: 100%;
  padding: var(--space-xxxl) 0 var(--space-xxxxxl);

  overflow: hidden;

  @media screen and (max-width: 1100px) {
    padding-top: var(--space-xxxl);
    padding-bottom: var(--space-xxxxl);
  }

  @media screen and (max-width: 700px) {
    padding: var(--space-xxl) var(--space-m) var(--space-xxxl);
  }
`;

export const HeroContainer = styled.div`
  width: min(100%, 1280px);

  margin: 0 auto;
`;

export const HeroContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(360px, 0.85fr);
  align-items: center;
  gap: clamp(var(--space-xxl), 6vw, var(--space-xxxxxl));

  @media screen and (max-width: 1050px) {
    grid-template-columns: minmax(0, 1fr) minmax(330px, 0.8fr);
    gap: var(--space-xxl);
  }

  @media screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: var(--space-xxxl);
  }
`;

export const HeroCopy = styled.div`
  width: 100%;
  max-width: 790px;
`;

export const HeroTitle = styled.h1`
  max-width: 900px;

  margin: 0;

  color: var(--grey7);

  font-size: clamp(3rem, 5.4vw, 5.25rem);
  line-height: 0.98;
  font-weight: 800;

  letter-spacing: -0.05em;

  @media screen and (max-width: 700px) {
    font-size: clamp(2.65rem, 12vw, 4.15rem);
    line-height: 1;
  }

  @media screen and (max-width: 430px) {
    font-size: clamp(2.4rem, 12vw, 3.3rem);
  }
`;

export const HeroDescription = styled.p`
  max-width: 670px;

  margin: var(--space-xl) 0 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: clamp(1rem, 1.4vw, 1.1rem);
  line-height: 1.8;
  font-weight: 500;

  @media screen and (max-width: 700px) {
    margin-top: var(--space-l);

    font-size: 1rem;
    line-height: 1.7;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-s);

  margin-top: var(--space-xl);

  @media screen and (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const OrderingNote = styled.div`
  width: min(100%, 680px);

  display: flex;
  align-items: flex-start;
  gap: var(--space-m);

  margin-top: var(--space-xl);
  padding: var(--space-m) var(--space-l);

  border: 1px solid rgba(230, 139, 7, 0.18);
  border-radius: var(--borderRadius);

  background: rgba(255, 240, 194, 0.42);

  box-shadow: var(--shadow-xs);

  @media screen and (max-width: 600px) {
    gap: var(--space-s);

    padding: var(--space-m);
  }
`;

export const OrderingIcon = styled.span`
  width: 40px;
  height: 40px;

  flex-shrink: 0;

  display: grid;
  place-items: center;

  border: 1px solid rgba(230, 139, 7, 0.16);
  border-radius: 50%;

  background: linear-gradient(135deg, var(--yellow1) 0%, var(--orange7) 100%);

  color: var(--orange5);

  font-size: 0.9rem;

  svg {
    display: block;
  }
`;

export const OrderingText = styled.p`
  margin: 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: 0.88rem;
  line-height: 1.65;
  font-weight: 500;

  strong {
    display: block;

    margin-bottom: var(--space-xxxs);

    color: var(--grey7);

    font-size: 0.94rem;
    font-weight: 800;
  }

  span {
    display: block;
  }
`;

export const HeroVisual = styled.div`
  width: 100%;

  position: relative;

  padding: clamp(var(--space-l), 3vw, var(--space-xl));

  border: 1px solid var(--grey);
  border-radius: var(--borderRadiusLarge);

  background: var(--white);

  box-shadow:
    0 24px 60px rgba(23, 23, 23, 0.08),
    0 3px 10px rgba(23, 23, 23, 0.035);

  @media screen and (max-width: 900px) {
    max-width: 680px;
  }

  @media screen and (max-width: 520px) {
    padding: var(--space-l);
  }
`;

export const VisualHeader = styled.div`
  position: relative;
  z-index: 2;
`;

export const VisualEyebrow = styled.p`
  margin: 0 0 var(--space-xs);

  color: var(--orange5);

  font-family: var(--mainFont);
  font-size: 0.7rem;
  line-height: 1.4;
  font-weight: 800;

  text-transform: uppercase;
  letter-spacing: 0.14em;
`;

export const VisualTitle = styled.h2`
  max-width: 390px;

  margin: 0;

  color: var(--grey7);

  font-size: clamp(1.65rem, 2.5vw, 2.25rem);
  line-height: 1.06;
  font-weight: 800;

  letter-spacing: -0.035em;
`;

export const VisualDescription = styled.p`
  max-width: 380px;

  margin: var(--space-s) 0 0;

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: 0.9rem;
  line-height: 1.65;
  font-weight: 500;
`;

export const ProductIconDisplay = styled.div`
  width: min(100%, 340px);
  aspect-ratio: 1.65 / 1;

  position: relative;

  margin: var(--space-xl) auto;

  @media screen and (max-width: 420px) {
    width: min(100%, 285px);
  }
`;

export const IconConnector = styled.div`
  width: 58%;
  aspect-ratio: 1;

  position: absolute;
  top: 50%;
  left: 50%;

  border: 1px dashed var(--orange1);
  border-radius: 50%;

  transform: translate(-50%, -50%);
`;

export const MainProductIcon = styled.div`
  width: 76px;
  height: 76px;

  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;

  display: grid;
  place-items: center;

  border: 1px solid rgba(230, 139, 7, 0.2);
  border-radius: 50%;

  background: var(--yellow1);

  color: var(--orange5);

  font-size: 1.85rem;

  box-shadow:
    0 16px 35px rgba(230, 139, 7, 0.15),
    0 2px 8px rgba(23, 23, 23, 0.05);

  transform: translate(-50%, -50%);

  svg {
    display: block;
  }

  @media screen and (max-width: 420px) {
    width: 66px;
    height: 66px;

    font-size: 1.55rem;
  }
`;

export const FloatingProductIcon = styled.div`
  width: 52px;
  height: 52px;

  position: absolute;
  z-index: 2;

  display: grid;
  place-items: center;

  border: 1px solid var(--grey);
  border-radius: var(--borderRadius);

  background: var(--white);

  color: var(--orange5);

  font-size: 1.1rem;

  box-shadow:
    0 12px 28px rgba(23, 23, 23, 0.08),
    0 2px 6px rgba(23, 23, 23, 0.04);

  ${({ $position }) => {
    switch ($position) {
      case 'topLeft':
        return `
          top: 3%;
          left: 9%;
          transform: rotate(-5deg);
        `;

      case 'topRight':
        return `
          top: 1%;
          right: 8%;
          transform: rotate(5deg);
        `;

      case 'bottomLeft':
        return `
          bottom: 2%;
          left: 12%;
          transform: rotate(4deg);
        `;

      case 'bottomRight':
        return `
          right: 10%;
          bottom: 4%;
          transform: rotate(-4deg);
        `;

      default:
        return '';
    }
  }}

  svg {
    display: block;
  }

  @media screen and (max-width: 420px) {
    width: 46px;
    height: 46px;

    font-size: 1rem;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-s);

  @media screen and (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  min-width: 0;

  display: flex;
  align-items: flex-start;
  gap: var(--space-s);

  padding: var(--space-m);

  border: 1px solid var(--grey);
  border-radius: var(--borderRadius);

  background: var(--cardBackground);

  transition:
    border-color 180ms ease,
    transform 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    border-color: var(--orange1);

    box-shadow: 0 10px 25px rgba(23, 23, 23, 0.06);

    transform: translateY(-2px);
  }
`;

export const StatIcon = styled.span`
  width: 30px;
  height: 30px;

  flex-shrink: 0;

  display: grid;
  place-items: center;

  border-radius: 50%;

  background: var(--yellow1);

  color: var(--orange5);

  font-size: 0.78rem;

  svg {
    display: block;
  }
`;

export const StatContent = styled.div`
  min-width: 0;
`;

export const StatValue = styled.strong`
  display: block;

  margin: 0;

  color: var(--grey7);

  font-family: var(--mainFont);
  font-size: 1.05rem;
  line-height: 1.2;
  font-weight: 800;
`;

export const StatLabel = styled.span`
  display: block;

  margin-top: var(--space-xxxs);

  color: var(--grey4);

  font-family: var(--mainFont);
  font-size: 0.76rem;
  line-height: 1.45;
  font-weight: 600;
`;
